import { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import './LogModal.css';

const LogModal = ({ isOpen, onClose, module, title, showEntityCode = true }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    status: '',
    store_id: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  const [closing, setClosing] = useState(false);
  const [closingDetail, setClosingDetail] = useState(false);
  const [stores, setStores] = useState([]);

  // Modal kapanış handler'ı
  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 250);
  };

  // Detay modal kapanış handler'ı
  const handleCloseDetail = () => {
    setClosingDetail(true);
    setTimeout(() => {
      setClosingDetail(false);
      setSelectedLog(null);
    }, 250);
  };

  // ESC tuşu ile kapatma
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedLog) {
          handleCloseDetail();
        } else {
          handleClose();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  // Modül isimleri (Türkçe)
  const moduleNames = {
    auth: 'Giriş/Çıkış',
    sales: 'Satış',
    products: 'Ürünler',
    customers: 'Müşteriler',
    repairs: 'Tamir',
    campaigns: 'Kampanyalar',
    inventory: 'Sayım',
    personnel: 'Personel',
    reports: 'Raporlar',
    settings: 'Ayarlar',
    scrap: 'Hurda',
    expenses: 'Masraflar',
    debts: 'Borçlar',
    terminal: 'Terminal',
    rates: 'Kurlar'
  };

  // Aksiyon isimleri (Türkçe)
  const actionNames = {
    login: 'Giriş',
    logout: 'Çıkış',
    login_failed: 'Başarısız Giriş',
    create: 'Oluştur',
    read: 'Görüntüle',
    update: 'Güncelle',
    delete: 'Sil',
    sale_complete: 'Satış Tamamla',
    sale_cancel: 'Satış İptal',
    refund: 'İade',
    exchange: 'Değişim',
    hold_sale: 'Sepet Beklet',
    resume_sale: 'Sepet Devam',
    apply_discount: 'İndirim Uygula',
    apply_campaign: 'Kampanya Uygula',
    price_change: 'Fiyat Değiştir',
    stock_in: 'Stok Alımı',
    stock_out: 'Stok Çıkış',
    stock_adjust: 'Stok Düzeltme',
    inventory_count: 'Sayım',
    debt_add: 'Borç Ekle',
    debt_payment: 'Borç Tahsilatı',
    bulk_delete: 'Toplu Silme',
    repair_status_change: 'Tamir Durumu',
    export: 'Dışa Aktar',
    import: 'İçe Aktar',
    backup: 'Yedekleme',
    restore: 'Geri Yükleme',
    view: 'Görüntüle',
    search: 'Arama'
  };

  // Durum renkleri
  const statusColors = {
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6'
  };

  // Durum isimleri
  const statusNames = {
    success: 'Başarılı',
    error: 'Hata',
    warning: 'Uyarı',
    info: 'Bilgi'
  };

  // Logları getir
  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: pagination.limit,
        module: module,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await fetch(`${API_URL}/logs?${params}`);
      const data = await response.json();

      setLogs(data.logs || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (error) {
      console.error('Log getirme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen, module]);

  // Mağaza filtresi için mağaza listesini yükle (yalnızca modal açıldığında, bir kez)
  useEffect(() => {
    if (!isOpen || stores.length > 0) return;
    fetch(`${API_URL}/stores`)
      .then(res => res.json())
      .then(data => setStores(Array.isArray(data) ? data : (data.stores || [])))
      .catch(() => setStores([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Filtreleri uygula
  const applyFilters = () => {
    fetchLogs(1);
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setFilters({
      action: '',
      status: '',
      store_id: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    setTimeout(() => fetchLogs(1), 100);
  };

  // Tarih formatla
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR');
  };

  // Log detaylarını güzel formatta göster
  const renderLogDetails = (log) => {
    let details = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
    if (!details) return null;

    // Eğer details içinde nested details varsa (eski yapı) unwrap et
    if (details.details && typeof details.details === 'object') {
      details = { ...details.details, entity_type: details.entity_type, entity_id: details.entity_id, status: details.status };
    }

    // Satış logu
    if (log.action === 'sale_complete' && details.items) {
      return (
        <div className="log-details-section">
          {/* Müşteri Bilgisi - obje veya ID olarak */}
          {(details.customer || details.customer_id) && (
            <div className="detail-card">
              <h4>👤 Müşteri</h4>
              <div className="detail-info">
                {details.customer ? (
                  <>
                    <span><strong>Ad:</strong> {details.customer.name || '-'}</span>
                    {details.customer.phone && <span><strong>Tel:</strong> {details.customer.phone}</span>}
                  </>
                ) : (
                  <span><strong>Müşteri No:</strong> #{details.customer_id}</span>
                )}
              </div>
            </div>
          )}

          {/* Personel Bilgisi */}
          {details.staff && details.staff.length > 0 && (
            <div className="detail-card">
              <h4>🏷️ Satış Sorumlusu</h4>
              <div className="detail-info">
                <span>{details.staff.map(s => s.name).join(', ')}</span>
              </div>
            </div>
          )}

          {/* Ürün Listesi */}
          <div className="detail-card">
            <h4>📦 Ürünler ({details.items_count || details.items.length} kalem)</h4>
            <table className="detail-items-table">
              <thead>
                <tr>
                  <th>Barkod</th>
                  <th>Ürün</th>
                  <th>Adet</th>
                  <th>B.Fiyat</th>
                  <th>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {details.items.map((item, idx) => {
                  const quantity = item.quantity || item.qty || 1;
                  const unitPrice = item.discounted_unit_price || item.unit_price || item.price || 0;
                  const totalPrice = item.total_price || (unitPrice * quantity);
                  return (
                    <tr key={idx}>
                      <td className="barcode">{item.barcode || item.id || '-'}</td>
                      <td>{item.name || item.product_name || '-'}</td>
                      <td className="qty">{quantity}</td>
                      <td className="price">₺{Number(unitPrice).toLocaleString('tr-TR')}</td>
                      <td className="price">₺{Number(totalPrice).toLocaleString('tr-TR')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Ödeme Detayları */}
          <div className="detail-card">
            <h4>💳 Ödeme Detayları</h4>
            <div className="payment-summary">
              {details.subtotal !== undefined && details.subtotal !== details.total && (
                <div className="payment-row">
                  <span>Ara Toplam:</span>
                  <span>₺{Number(details.subtotal || 0).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.discount > 0 && (
                <div className="payment-row discount">
                  <span>İndirim:</span>
                  <span>-₺{Number(details.discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.campaign_discount > 0 && (
                <div className="payment-row discount">
                  <span>Kampanya İndirimi{details.campaign?.name ? ` (${details.campaign.name})` : ''}:</span>
                  <span>-₺{Number(details.campaign_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.scrap_discount > 0 && (
                <div className="payment-row discount">
                  <span>Hurda İndirimi:</span>
                  <span>-₺{Number(details.scrap_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.gift_card_discount > 0 && (
                <div className="payment-row discount">
                  <span>Bakiye:</span>
                  <span>-₺{Number(details.gift_card_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.scrap_debt_discount > 0 && (
                <div className="payment-row discount">
                  <span>Hurda Alacağı:</span>
                  <span>-₺{Number(details.scrap_debt_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              <div className="payment-row total">
                <span>TOPLAM:</span>
                <span>₺{Number(details.total || 0).toLocaleString('tr-TR')}</span>
              </div>
              {details.payment_method && (
                <div className="payment-row method">
                  <span>Ödeme Yöntemi:</span>
                  <span>{details.payment_method}</span>
                </div>
              )}
              {details.payment_details && details.payment_details.length > 0 && (
                <div className="payment-breakdown">
                  <strong>Ödeme Dağılımı:</strong>
                  {details.payment_details.map((p, i) => (
                    <div key={i} className="payment-item">
                      {p.method}: {p.currency !== 'TRY' && p.currency ? `${p.amount} ${p.currency} = ` : ''}₺{Number(p.amount_tl || p.amount).toLocaleString('tr-TR')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // İade logu
    if (log.action === 'refund' && details.return_items) {
      return (
        <div className="log-details-section">
          {details.customer && (
            <div className="detail-card">
              <h4>👤 Müşteri</h4>
              <div className="detail-info">
                <span><strong>Ad:</strong> {details.customer.name || '-'}</span>
                <span><strong>Tel:</strong> {details.customer.phone || '-'}</span>
              </div>
            </div>
          )}

          <div className="detail-card">
            <h4>↩️ İade Edilen Ürünler ({details.return_items.length} kalem)</h4>
            <table className="detail-items-table">
              <thead>
                <tr>
                  <th>Barkod</th>
                  <th>Ürün</th>
                  <th>Adet</th>
                  <th>B.Fiyat</th>
                  <th>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {details.return_items.map((item, idx) => (
                  <tr key={idx}>
                    <td className="barcode">{item.barcode || '-'}</td>
                    <td>{item.name}</td>
                    <td className="qty">{item.quantity}</td>
                    <td className="price">₺{Number(item.unit_price).toLocaleString('tr-TR')}</td>
                    <td className="price">₺{Number(item.total_price).toLocaleString('tr-TR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="detail-card">
            <h4>💰 İade Özeti</h4>
            <div className="payment-summary">
              <div className="payment-row total">
                <span>İade Tutarı:</span>
                <span>₺{Number(details.refund_total).toLocaleString('tr-TR')}</span>
              </div>
              <div className="payment-row">
                <span>Ödeme Yöntemi:</span>
                <span>{details.payment_method}</span>
              </div>
              <div className="payment-row">
                <span>Orijinal Satış:</span>
                <span>#{details.original_sale_id}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Değişim logu
    if (log.action === 'exchange' && details.return_items) {
      return (
        <div className="log-details-section">
          {details.customer && (
            <div className="detail-card">
              <h4>👤 Müşteri</h4>
              <div className="detail-info">
                <span><strong>Ad:</strong> {details.customer.name || '-'}</span>
                <span><strong>Tel:</strong> {details.customer.phone || '-'}</span>
              </div>
            </div>
          )}

          <div className="detail-card">
            <h4>↩️ İade Edilen Ürünler ({details.return_items.length} kalem)</h4>
            <table className="detail-items-table">
              <thead>
                <tr>
                  <th>Barkod</th>
                  <th>Ürün</th>
                  <th>Adet</th>
                  <th>B.Fiyat</th>
                  <th>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {details.return_items.map((item, idx) => (
                  <tr key={idx}>
                    <td className="barcode">{item.barcode || '-'}</td>
                    <td>{item.name}</td>
                    <td className="qty">{item.quantity}</td>
                    <td className="price">₺{Number(item.unit_price).toLocaleString('tr-TR')}</td>
                    <td className="price">₺{Number(item.total_price).toLocaleString('tr-TR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {details.new_items && details.new_items.length > 0 && (
            <div className="detail-card">
              <h4>📦 Yeni Verilen Ürünler ({details.new_items.length} kalem)</h4>
              <table className="detail-items-table">
                <thead>
                  <tr>
                    <th>Barkod</th>
                    <th>Ürün</th>
                    <th>Adet</th>
                    <th>B.Fiyat</th>
                    <th>Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {details.new_items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="barcode">{item.barcode || '-'}</td>
                      <td>{item.name}</td>
                      <td className="qty">{item.quantity}</td>
                      <td className="price">₺{Number(item.unit_price).toLocaleString('tr-TR')}</td>
                      <td className="price">₺{Number(item.total_price).toLocaleString('tr-TR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="detail-card">
            <h4>💰 Değişim Özeti</h4>
            <div className="payment-summary">
              <div className="payment-row">
                <span>İade Toplam:</span>
                <span>₺{Number(details.return_total).toLocaleString('tr-TR')}</span>
              </div>
              <div className="payment-row">
                <span>Yeni Ürünler Toplam:</span>
                <span>₺{Number(details.new_items_total).toLocaleString('tr-TR')}</span>
              </div>
              <div className="payment-row total">
                <span>FARK:</span>
                <span style={{ color: details.difference >= 0 ? '#ef4444' : '#10b981' }}>
                  {details.difference >= 0 ? '' : '+'}₺{Math.abs(Number(details.difference)).toLocaleString('tr-TR')}
                </span>
              </div>
              <div className="payment-row">
                <span>Ödeme Yöntemi:</span>
                <span>{details.payment_method}</span>
              </div>
              <div className="payment-row">
                <span>Orijinal Satış:</span>
                <span>#{details.original_sale_id}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Tahsilat logu
    if (log.action === 'debt_payment' && details.payment) {
      return (
        <div className="log-details-section">
          {details.customer && (
            <div className="detail-card">
              <h4>👤 Müşteri</h4>
              <div className="detail-info">
                <span><strong>Ad:</strong> {details.customer.name || '-'}</span>
                <span><strong>Tel:</strong> {details.customer.phone || '-'}</span>
              </div>
            </div>
          )}

          <div className="detail-card">
            <h4>📋 Borç Bilgisi</h4>
            <div className="detail-info">
              <span><strong>Borç ID:</strong> #{details.debt?.id}</span>
              <span><strong>Açıklama:</strong> {details.debt?.description || '-'}</span>
              <span><strong>Borç Tipi:</strong> {details.debt?.debt_type === 'TL' ? 'TL Borcu' : details.debt?.debt_type === 'GRAM' ? 'Altın Borcu' : 'Gümüş Borcu'}</span>
              <span><strong>Orijinal Tutar:</strong> ₺{Number(details.debt?.original_amount).toLocaleString('tr-TR')}</span>
            </div>
          </div>

          <div className="detail-card">
            <h4>💳 Ödeme Detayları</h4>
            <div className="payment-summary">
              <div className="payment-row total">
                <span>Ödenen Tutar:</span>
                <span>₺{Number(details.payment.amount).toLocaleString('tr-TR')}</span>
              </div>
              <div className="payment-row">
                <span>Ödeme Yöntemi:</span>
                <span>{details.payment.payment_method}</span>
              </div>
              {details.payment.is_scrap_payment && details.payment.scrap_details && (
                <div className="payment-row">
                  <span>Hurda Bilgisi:</span>
                  <span>{details.payment.scrap_details.metal_type === 'gold' ? 'Altın' : 'Gümüş'} {details.payment.scrap_details.purity} Ayar - {details.payment.scrap_details.pure_weight?.toFixed(2)} gr</span>
                </div>
              )}
              <div className="payment-row">
                <span>Borç Durumu:</span>
                <span style={{ color: details.result?.is_fully_paid ? '#10b981' : '#f59e0b' }}>
                  {details.result?.is_fully_paid ? '✓ Tamamen Ödendi' : `Kalan: ₺${Number(details.result?.remaining_amount).toLocaleString('tr-TR')}`}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Hurda alımı logu
    if (log.action === 'create' && log.module === 'scrap' && details.metal_type) {
      return (
        <div className="log-details-section">
          {details.customer && (
            <div className="detail-card">
              <h4>👤 Müşteri</h4>
              <div className="detail-info">
                <span><strong>Ad:</strong> {details.customer.name || '-'}</span>
                <span><strong>Tel:</strong> {details.customer.phone || '-'}</span>
              </div>
            </div>
          )}

          <div className="detail-card">
            <h4>⚖️ Hurda Detayları</h4>
            <div className="detail-info">
              <span><strong>Metal Türü:</strong> {details.metal_type_label || (details.metal_type === 'gold' ? 'Altın' : 'Gümüş')}</span>
              <span><strong>Ayar:</strong> {details.purity}</span>
              <span><strong>Brüt Ağırlık:</strong> {Number(details.gross_weight).toFixed(2)} gr</span>
              <span><strong>Fire:</strong> %{details.waste_percent}</span>
              <span><strong>Saf Ağırlık:</strong> {Number(details.pure_weight).toFixed(2)} gr</span>
              <span><strong>Gram Fiyatı:</strong> ₺{Number(details.price_per_gram).toLocaleString('tr-TR')}</span>
              {details.quantity > 1 && <span><strong>Adet:</strong> {details.quantity}</span>}
            </div>
          </div>

          <div className="detail-card">
            <h4>💰 Ödeme</h4>
            <div className="payment-summary">
              <div className="payment-row total">
                <span>Toplam Tutar:</span>
                <span>₺{Number(details.total_amount).toLocaleString('tr-TR')}</span>
              </div>
              <div className="payment-row">
                <span>Ödeme Yöntemi:</span>
                <span>{details.payment_method}</span>
              </div>
              {details.sale_id && (
                <div className="payment-row">
                  <span>Bağlı Satış:</span>
                  <span>#{details.sale_id}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Satış düzenleme logu
    if (log.action === 'sale_edit' && details.items) {
      return (
        <div className="log-details-section">
          {details.customer && (
            <div className="detail-card">
              <h4>👤 Müşteri</h4>
              <div className="detail-info">
                <span><strong>Ad:</strong> {details.customer.name || '-'}</span>
              </div>
            </div>
          )}

          <div className="detail-card">
            <h4>✏️ Değişiklik Özeti</h4>
            <div className="payment-summary">
              <div className="payment-row">
                <span>Eski Toplam:</span>
                <span>₺{Number(details.old_total).toLocaleString('tr-TR')}</span>
              </div>
              <div className="payment-row">
                <span>Yeni Toplam:</span>
                <span>₺{Number(details.new_total).toLocaleString('tr-TR')}</span>
              </div>
              <div className="payment-row total">
                <span>FARK:</span>
                <span style={{ color: details.difference >= 0 ? '#dc2626' : '#10b981' }}>
                  {details.difference >= 0 ? '+' : ''}₺{Number(details.difference).toLocaleString('tr-TR')}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-card">
            <h4>📦 Düzenlenen Ürünler ({details.items.length} kalem)</h4>
            <table className="detail-items-table">
              <thead>
                <tr>
                  <th>Barkod</th>
                  <th>Ürün</th>
                  <th>Eski Fiyat</th>
                  <th>Yeni Fiyat</th>
                  <th>Eski Adet</th>
                  <th>Yeni Adet</th>
                </tr>
              </thead>
              <tbody>
                {details.items.map((item, idx) => (
                  <tr key={idx} style={{ background: (item.price_changed || item.quantity_changed) ? 'rgba(245, 158, 11, 0.1)' : 'transparent' }}>
                    <td className="barcode">{item.barcode || '-'}</td>
                    <td>{item.product_name}</td>
                    <td className="price" style={{ color: item.price_changed ? '#dc2626' : 'inherit' }}>
                      ₺{Number(item.old_price).toLocaleString('tr-TR')}
                    </td>
                    <td className="price" style={{ color: item.price_changed ? '#10b981' : 'inherit' }}>
                      ₺{Number(item.new_price).toLocaleString('tr-TR')}
                    </td>
                    <td className="qty" style={{ color: item.quantity_changed ? '#dc2626' : 'inherit' }}>
                      {item.old_quantity}
                    </td>
                    <td className="qty" style={{ color: item.quantity_changed ? '#10b981' : 'inherit' }}>
                      {item.new_quantity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Genel satış logu (items içeren tüm loglar için - farklı formatları destekler)
    if (details.items && Array.isArray(details.items) && details.items.length > 0) {
      return (
        <div className="log-details-section">
          {/* Müşteri Bilgisi - obje veya ID olarak */}
          {(details.customer || details.customer_id) && (
            <div className="detail-card">
              <h4>👤 Müşteri</h4>
              <div className="detail-info">
                {details.customer ? (
                  <>
                    <span><strong>Ad:</strong> {details.customer.name || '-'}</span>
                    {details.customer.phone && <span><strong>Tel:</strong> {details.customer.phone}</span>}
                  </>
                ) : (
                  <span><strong>Müşteri No:</strong> #{details.customer_id}</span>
                )}
              </div>
            </div>
          )}

          {/* Personel Bilgisi */}
          {details.staff && details.staff.length > 0 && (
            <div className="detail-card">
              <h4>🏷️ Satış Sorumlusu</h4>
              <div className="detail-info">
                <span>{details.staff.map(s => s.name).join(', ')}</span>
              </div>
            </div>
          )}

          {/* Ürün Listesi */}
          <div className="detail-card">
            <h4>📦 Ürünler ({details.items_count || details.items.length} kalem)</h4>
            <table className="detail-items-table">
              <thead>
                <tr>
                  <th>Barkod</th>
                  <th>Ürün</th>
                  <th>Adet</th>
                  <th>B.Fiyat</th>
                  <th>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {details.items.map((item, idx) => {
                  const quantity = item.quantity || item.qty || 1;
                  const unitPrice = item.discounted_unit_price || item.unit_price || item.price || 0;
                  const totalPrice = item.total_price || (unitPrice * quantity);
                  return (
                    <tr key={idx}>
                      <td className="barcode">{item.barcode || item.id || '-'}</td>
                      <td>{item.name || item.product_name || '-'}</td>
                      <td className="qty">{quantity}</td>
                      <td className="price">₺{Number(unitPrice).toLocaleString('tr-TR')}</td>
                      <td className="price">₺{Number(totalPrice).toLocaleString('tr-TR')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Ödeme Detayları */}
          <div className="detail-card">
            <h4>💳 Ödeme Detayları</h4>
            <div className="payment-summary">
              {(details.subtotal !== undefined && details.subtotal !== details.total) && (
                <div className="payment-row">
                  <span>Ara Toplam:</span>
                  <span>₺{Number(details.subtotal || 0).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.discount > 0 && (
                <div className="payment-row discount">
                  <span>İndirim:</span>
                  <span>-₺{Number(details.discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.campaign_discount > 0 && (
                <div className="payment-row discount">
                  <span>Kampanya İndirimi{details.campaign?.name ? ` (${details.campaign.name})` : ''}:</span>
                  <span>-₺{Number(details.campaign_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.scrap_discount > 0 && (
                <div className="payment-row discount">
                  <span>Hurda İndirimi:</span>
                  <span>-₺{Number(details.scrap_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.gift_card_discount > 0 && (
                <div className="payment-row discount">
                  <span>Bakiye:</span>
                  <span>-₺{Number(details.gift_card_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              {details.scrap_debt_discount > 0 && (
                <div className="payment-row discount">
                  <span>Hurda Alacağı:</span>
                  <span>-₺{Number(details.scrap_debt_discount).toLocaleString('tr-TR')}</span>
                </div>
              )}
              <div className="payment-row total">
                <span>TOPLAM:</span>
                <span>₺{Number(details.total || 0).toLocaleString('tr-TR')}</span>
              </div>
              {details.payment_method && (
                <div className="payment-row method">
                  <span>Ödeme Yöntemi:</span>
                  <span>{details.payment_method}</span>
                </div>
              )}
              {details.payment_details && details.payment_details.length > 0 && (
                <div className="payment-breakdown">
                  <strong>Ödeme Dağılımı:</strong>
                  {details.payment_details.map((p, i) => (
                    <div key={i} className="payment-item">
                      {p.method}: {p.currency !== 'TRY' && p.currency ? `${p.amount} ${p.currency} = ` : ''}₺{Number(p.amount_tl || p.amount).toLocaleString('tr-TR')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Label formatı - önce tanımlanmalı
    const formatLabel = (key) => {
      const labels = {
        sale_id: 'Satış ID',
        store_sale_number: 'Fiş No',
        total: 'Toplam',
        subtotal: 'Ara Toplam',
        discount: 'İndirim',
        campaign_discount: 'Kampanya İndirimi',
        scrap_discount: 'Hurda İndirimi',
        gift_card_discount: 'Bakiye',
        scrap_debt_discount: 'Hurda Alacağı',
        customer_gift_card_discount: 'Müşteri Bakiyesi',
        payment_method: 'Ödeme Yöntemi',
        payment_details: 'Ödeme Detayları',
        customer: 'Müşteri',
        staff: 'Personel',
        campaign: 'Kampanya',
        items: 'Ürünler',
        return_items: 'İade Ürünleri',
        new_items: 'Yeni Ürünler',
        original_sale_id: 'Orijinal Satış',
        new_sale_id: 'Yeni Satış',
        return_total: 'İade Toplam',
        new_items_total: 'Yeni Ürün Toplam',
        difference: 'Fark',
        refund_total: 'İade Tutarı',
        metal_type: 'Metal Türü',
        metal_type_label: 'Metal',
        purity: 'Ayar',
        gross_weight: 'Brüt Ağırlık',
        pure_weight: 'Saf Ağırlık',
        waste_percent: 'Fire %',
        price_per_gram: 'Gram Fiyatı',
        total_amount: 'Toplam Tutar',
        quantity: 'Adet',
        debt: 'Borç Bilgisi',
        payment: 'Ödeme',
        result: 'Sonuç',
        is_fully_paid: 'Tamamen Ödendi',
        remaining_amount: 'Kalan Tutar',
        old_total: 'Eski Toplam',
        new_total: 'Yeni Toplam',
        barcode: 'Barkod',
        name: 'Ad',
        phone: 'Telefon',
        notes: 'Notlar',
        description: 'Açıklama',
        code: 'Kod',
        amount: 'Tutar',
        reason: 'Sebep',
        product_id: 'Ürün ID',
        unit_price: 'Birim Fiyat',
        discounted_unit_price: 'İndirimli Fiyat',
        total_price: 'Toplam Fiyat',
        discount_percent: 'İndirim %',
        discount_amount: 'İndirim Tutarı',
        applied_discount: 'Uygulanan İndirim',
        method: 'Yöntem',
        currency: 'Para Birimi',
        amount_tl: 'TL Tutarı',
        rate: 'Kur',
        id: 'ID',
        debt_type: 'Borç Tipi',
        original_amount: 'Orijinal Tutar',
        is_scrap_payment: 'Hurda Ödemesi',
        scrap_details: 'Hurda Detayı',
        created_at: 'Oluşturma Tarihi',
        updated_at: 'Güncelleme Tarihi',
        customer_id: 'Müşteri ID',
        store_id: 'Mağaza ID',
        user_id: 'Kullanıcı ID',
        product_name: 'Ürün Adı',
        category: 'Kategori',
        weight: 'Ağırlık',
        carat: 'Ayar',
        labor_cost: 'İşçilik',
        labor: 'İşçilik',
        cost: 'Maliyet',
        profit: 'Kar',
        margin: 'Kar Marjı'
      };
      return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Diğer loglar için okunabilir format
    const renderValue = (value, key, depth = 0) => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır';
      if (typeof value === 'number') {
        // Para birimi gibi görünen değerler
        if (key && (key.includes('price') || key.includes('total') || key.includes('amount') || key.includes('discount') || key.includes('_tl'))) {
          return `₺${Number(value).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
        }
        // Ağırlık gibi görünen değerler
        if (key && (key.includes('weight') || key.includes('gram'))) {
          return `${Number(value).toFixed(2)} gr`;
        }
        // Yüzde değerleri
        if (key && (key.includes('percent') || key.includes('rate'))) {
          return `%${value}`;
        }
        return value.toLocaleString('tr-TR');
      }
      if (Array.isArray(value)) {
        if (value.length === 0) return '-';
        // Ürün listesi varsa tablo olarak göster
        if (value[0] && (value[0].name || value[0].product_name || value[0].barcode)) {
          return (
            <table className="detail-items-table" style={{ marginTop: '8px' }}>
              <thead>
                <tr>
                  <th>Barkod</th>
                  <th>Ürün</th>
                  <th>Adet</th>
                  <th>Fiyat</th>
                </tr>
              </thead>
              <tbody>
                {value.map((item, idx) => (
                  <tr key={idx}>
                    <td className="barcode">{item.barcode || '-'}</td>
                    <td>{item.name || item.product_name || '-'}</td>
                    <td className="qty">{item.quantity || item.qty || 1}</td>
                    <td className="price">₺{Number(item.price || item.unit_price || item.total_price || item.discounted_unit_price || 0).toLocaleString('tr-TR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        // Ödeme detayları listesi
        if (value[0] && (value[0].method || value[0].payment_method)) {
          return (
            <div className="payment-list">
              {value.map((p, i) => (
                <div key={i} className="payment-item">
                  <strong>{p.method || p.payment_method}:</strong> {p.currency && p.currency !== 'TRY' ? `${p.amount} ${p.currency} = ` : ''}₺{Number(p.amount_tl || p.amount || 0).toLocaleString('tr-TR')}
                </div>
              ))}
            </div>
          );
        }
        // Staff listesi
        if (value[0] && value[0].name && !value[0].barcode && !value[0].method) {
          return value.map(v => v.name).join(', ');
        }
        // Basit array
        if (typeof value[0] !== 'object') {
          return value.join(', ');
        }
        // Diğer obje arrayleri için liste olarak göster
        return (
          <div className="array-list">
            {value.map((item, idx) => (
              <div key={idx} className="array-item">
                {Object.entries(item).map(([k, v]) => {
                  // Internal alanları atla
                  if (['entity_type', 'entity_id', 'status'].includes(k)) return null;
                  let displayValue;
                  if (v === null || v === undefined) {
                    displayValue = '-';
                  } else if (typeof v === 'object') {
                    if (v.name) {
                      displayValue = v.name + (v.phone ? ` (${v.phone})` : '');
                    } else if (Array.isArray(v)) {
                      displayValue = v.length > 0 ? `${v.length} öğe` : '-';
                    } else {
                      // Basit obje için key-value göster
                      displayValue = Object.entries(v).map(([subK, subV]) =>
                        `${formatLabel(subK)}: ${subV}`
                      ).join(', ') || '-';
                    }
                  } else {
                    displayValue = renderValue(v, k, depth + 1);
                  }
                  return (
                    <span key={k} className="array-item-field">
                      <strong>{formatLabel(k)}:</strong> {displayValue}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        );
      }
      if (typeof value === 'object') {
        // Müşteri, personel gibi nested objeler
        if (value.name) {
          let result = value.name;
          if (value.phone) result += ` (${value.phone})`;
          if (value.id && !value.phone) result += ` #${value.id}`;
          return result;
        }
        // Basit key-value objeleri için satır satır göster (depth kontrolü ile sonsuz döngü önle)
        if (depth < 2) {
          return (
            <div className="nested-details">
              {Object.entries(value).map(([k, v]) => {
                // Internal alanları atla
                if (['entity_type', 'entity_id', 'status'].includes(k)) return null;
                return (
                  <div key={k} className="nested-row">
                    <strong>{formatLabel(k)}:</strong> {renderValue(v, k, depth + 1)}
                  </div>
                );
              })}
            </div>
          );
        }
        // Çok derin objelerde sadece önemli alanları göster
        if (value.name) return value.name;
        if (value.id) return `#${value.id}`;
        return '-';
      }
      return String(value);
    };

    // Önemli alanları önce, detayları sonra göster
    const priorityKeys = ['customer', 'staff', 'items', 'return_items', 'new_items', 'total', 'subtotal', 'discount', 'payment_method', 'payment_details'];
    const sortedEntries = Object.entries(details).sort(([a], [b]) => {
      const aIdx = priorityKeys.indexOf(a);
      const bIdx = priorityKeys.indexOf(b);
      if (aIdx === -1 && bIdx === -1) return 0;
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });

    return (
      <div className="log-details-section">
        <div className="detail-card">
          <h4>📋 İşlem Detayları</h4>
          <div className="generic-details">
            {sortedEntries.map(([key, value]) => {
              // Bazı internal alanları atla
              if (['entity_type', 'entity_id', 'status'].includes(key)) return null;

              const renderedValue = renderValue(value, key);
              const isTable = typeof renderedValue === 'object' && renderedValue?.type === 'table';

              return (
                <div key={key} className={`generic-detail-row ${isTable ? 'full-width' : ''}`}>
                  <span className="detail-label">{formatLabel(key)}:</span>
                  <span className="detail-value">{renderedValue}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`log-modal-overlay ${closing ? 'closing' : ''}`}>
      <div className="log-modal" onClick={(e) => e.stopPropagation()}>
        <div className="log-modal-header">
          <h2>{title || moduleNames[module] || module} - İşlem Geçmişi</h2>
          <button className="log-modal-close" onClick={handleClose}>✕</button>
        </div>

        {/* Filtreler */}
        <div className="log-modal-filters">
          <input
            type="text"
            placeholder="Ara..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Tüm Durumlar</option>
            {Object.entries(statusNames).map(([key, name]) => (
              <option key={key} value={key}>{name}</option>
            ))}
          </select>
          <select value={filters.store_id} onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}>
            <option value="">Tüm Mağazalar</option>
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            placeholder="Başlangıç"
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            placeholder="Bitiş"
          />
          <button className="log-filter-btn" onClick={applyFilters}>Filtrele</button>
          <button className="log-clear-btn" onClick={clearFilters}>Temizle</button>
        </div>

        {/* Log Listesi */}
        <div className="log-modal-content">
          {loading ? (
            <div className="log-loading">Yükleniyor...</div>
          ) : logs.length === 0 ? (
            <div className="log-empty">Kayıt bulunamadı</div>
          ) : (
            <table className="log-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Kullanıcı</th>
                  <th>Mağaza</th>
                  {showEntityCode && <th>Fiş No</th>}
                  <th>İşlem</th>
                  <th>Açıklama</th>
                  <th>Durum</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className={`log-row-${log.status}`}>
                    <td className="log-date">{formatDate(log.created_at)}</td>
                    <td className="log-user">
                      <span className="user-name">{log.user_name || '-'}</span>
                      {log.user_role && <span className="user-role">{log.user_role}</span>}
                    </td>
                    <td className="log-store">{log.store_name || '-'}</td>
                    {showEntityCode && <td className="log-code">{log.entity_code || '-'}</td>}
                    <td className="log-action">{actionNames[log.action] || log.action}</td>
                    <td className="log-desc">{log.description}</td>
                    <td>
                      <span className="log-status" style={{ background: statusColors[log.status] }}>
                        {statusNames[log.status]}
                      </span>
                    </td>
                    <td>
                      {log.details && (
                        <button className="log-detail-btn" onClick={() => setSelectedLog(log)}>
                          Detay
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Sayfalama */}
        {pagination.totalPages > 1 && (
          <div className="log-modal-pagination">
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchLogs(pagination.page - 1)}
            >
              ← Önceki
            </button>
            <span>Sayfa {pagination.page} / {pagination.totalPages} ({pagination.total} kayıt)</span>
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => fetchLogs(pagination.page + 1)}
            >
              Sonraki →
            </button>
          </div>
        )}

        {/* Detay Modal */}
        {selectedLog && (
          <div className={`log-detail-overlay ${closingDetail ? 'closing' : ''}`} onClick={handleCloseDetail}>
            <div className="log-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="log-detail-header">
                <h3>Log Detayı</h3>
                <button onClick={handleCloseDetail}>✕</button>
              </div>
              <div className="log-detail-content">
                <div className="detail-row">
                  <label>Tarih:</label>
                  <span>{formatDate(selectedLog.created_at)}</span>
                </div>
                <div className="detail-row">
                  <label>Kullanıcı:</label>
                  <span>{selectedLog.user_name || '-'} ({selectedLog.user_role || '-'})</span>
                </div>
                <div className="detail-row">
                  <label>Mağaza:</label>
                  <span>{selectedLog.store_name || '-'}</span>
                </div>
                {selectedLog.entity_code && (
                  <div className="detail-row">
                    <label>Fiş No:</label>
                    <span className="entity-code-badge">{selectedLog.entity_code}</span>
                  </div>
                )}
                <div className="detail-row">
                  <label>İşlem:</label>
                  <span>{actionNames[selectedLog.action] || selectedLog.action}</span>
                </div>
                <div className="detail-row">
                  <label>Durum:</label>
                  <span className="log-status" style={{ background: statusColors[selectedLog.status] }}>
                    {statusNames[selectedLog.status]}
                  </span>
                </div>
                <div className="detail-row full">
                  <label>Açıklama:</label>
                  <span>{selectedLog.description}</span>
                </div>
                {selectedLog.entity_type && (
                  <div className="detail-row">
                    <label>Kayıt:</label>
                    <span>{selectedLog.entity_type} #{selectedLog.entity_id}</span>
                  </div>
                )}
                <div className="detail-row">
                  <label>IP Adresi:</label>
                  <span>{selectedLog.ip_address || '-'}</span>
                </div>
                {selectedLog.details && renderLogDetails(selectedLog)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogModal;
